import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  AccountTree as AccountTreeIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  MoreVert as MoreVertIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  Sync as SyncIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CalendarToday as CalendarTodayIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface PrimaveraSchedule {
  _id: string;
  name: string;
  description: string;
  project: string;
  primaveraSettings: {
    projectId: string;
    baselineDate: string;
    dataDate: string;
    finishDate: string;
    criticalPathMethod: 'CPM' | 'PERT';
    calendarId: string;
    resourceLeveling: boolean;
    costLoading: boolean;
  };
  metrics: {
    totalFloat: number;
    freeFloat: number;
    criticalPathLength: number;
    schedulePerformanceIndex: number;
    costPerformanceIndex: number;
    estimateAtCompletion: number;
  };
  status: string;
}

interface Activity {
  _id: string;
  activityId: string;
  name: string;
  description: string;
  originalDuration: number;
  remainingDuration: number;
  actualDuration: number;
  earlyStart: string;
  earlyFinish: string;
  lateStart: string;
  lateFinish: string;
  totalFloat: number;
  freeFloat: number;
  criticalPath: boolean;
  status: 'not-started' | 'in-progress' | 'completed';
  percentComplete: number;
  actualStart?: string;
  actualFinish?: string;
  budgetedCost: number;
  actualCost: number;
  earnedValue: number;
  resourceAssignments: Array<{
    resource: string;
    units: number;
    cost: number;
  }>;
  predecessors: Array<{
    activity: string;
    relationship: 'FS' | 'SS' | 'FF' | 'SF';
    lag: number;
  }>;
  successors: Array<{
    activity: string;
    relationship: 'FS' | 'SS' | 'FF' | 'SF';
    lag: number;
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`primavera-tabpanel-${index}`}
      aria-labelledby={`primavera-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PrimaveraView: React.FC = () => {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();
  
  const [schedule, setSchedule] = useState<PrimaveraSchedule | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [availableSchedules, setAvailableSchedules] = useState<PrimaveraSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);

  useEffect(() => {
    if (scheduleId) {
      fetchPrimaveraData();
    } else {
      // No scheduleId provided, fetch available schedules
      fetchAvailableSchedules();
    }
  }, [scheduleId]);

  const fetchAvailableSchedules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/schedules');
      setAvailableSchedules(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch available schedules');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrimaveraData = async () => {
    try {
      setLoading(true);
      const [scheduleResponse, activitiesResponse] = await Promise.all([
        api.get(`/schedules/${scheduleId}/primavera`),
        api.get(`/schedules/${scheduleId}/activities`)
      ]);
      
      setSchedule(scheduleResponse.data.data);
      setActivities(activitiesResponse.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch Primavera data');
    } finally {
      setLoading(false);
    }
  };

  const handleImportP6 = async (file: File) => {
    try {
      setSyncInProgress(true);
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/schedules/${scheduleId}/import-p6`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportDialogOpen(false);
      fetchPrimaveraData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to import P6 file');
    } finally {
      setSyncInProgress(false);
    }
  };

  const handleExportP6 = async () => {
    try {
      const response = await api.get(`/schedules/${scheduleId}/export-p6`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${schedule?.name || 'schedule'}.xer`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export P6 file');
    }
  };

  const handleCalculateCriticalPath = async () => {
    try {
      setSyncInProgress(true);
      await api.post(`/schedules/${scheduleId}/calculate-critical-path`);
      fetchPrimaveraData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to calculate critical path');
    } finally {
      setSyncInProgress(false);
    }
  };

  const handleResourceLeveling = async () => {
    try {
      setSyncInProgress(true);
      await api.post(`/schedules/${scheduleId}/resource-leveling`);
      fetchPrimaveraData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to perform resource leveling');
    } finally {
      setSyncInProgress(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'primary';
      case 'not-started': return 'default';
      default: return 'default';
    }
  };

  const getFloatColor = (totalFloat: number) => {
    if (totalFloat === 0) return '#f44336'; // Critical
    if (totalFloat <= 5) return '#ff9800'; // Near critical
    return '#4caf50'; // Non-critical
  };

  const renderActivitiesTable = () => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Activity ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Original Duration</TableCell>
            <TableCell>Remaining Duration</TableCell>
            <TableCell>Early Start</TableCell>
            <TableCell>Early Finish</TableCell>
            <TableCell>Total Float</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>% Complete</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {activities.map((activity) => (
            <TableRow
              key={activity._id}
              sx={{
                backgroundColor: activity.criticalPath ? 'rgba(244, 67, 54, 0.1)' : 'inherit',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {activity.criticalPath && (
                    <Tooltip title="Critical Path">
                      <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    </Tooltip>
                  )}
                  {activity.activityId}
                </Box>
              </TableCell>
              <TableCell>{activity.name}</TableCell>
              <TableCell>{activity.originalDuration}d</TableCell>
              <TableCell>{activity.remainingDuration}d</TableCell>
              <TableCell>{new Date(activity.earlyStart).toLocaleDateString()}</TableCell>
              <TableCell>{new Date(activity.earlyFinish).toLocaleDateString()}</TableCell>
              <TableCell>
                <Chip
                  label={`${activity.totalFloat}d`}
                  size="small"
                  sx={{
                    backgroundColor: getFloatColor(activity.totalFloat),
                    color: 'white'
                  }}
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={activity.status}
                  size="small"
                  color={getStatusColor(activity.status) as any}
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={activity.percentComplete}
                    sx={{ width: 60, height: 6 }}
                  />
                  <Typography variant="caption">
                    {activity.percentComplete}%
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setSelectedActivity(activity);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMetrics = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' }, gap: 3 }}>
      <Box>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Critical Path Length
                </Typography>
                <Typography variant="h4">
                  {schedule?.metrics.criticalPathLength || 0}d
                </Typography>
              </Box>
              <TimelineIcon color="error" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Box>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Schedule Performance Index
                </Typography>
                <Typography variant="h4">
                  {schedule?.metrics.schedulePerformanceIndex?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
              <SpeedIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Box>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Cost Performance Index
                </Typography>
                <Typography variant="h4">
                  {schedule?.metrics.costPerformanceIndex?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
              <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Box>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Estimate at Completion
                </Typography>
                <Typography variant="h4">
                  ${(schedule?.metrics.estimateAtCompletion || 0).toLocaleString()}
                </Typography>
              </Box>
              <AssessmentIcon color="warning" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  const renderCriticalPath = () => {
    const criticalActivities = activities.filter(activity => activity.criticalPath);
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Critical Path Activities ({criticalActivities.length})
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Activity ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Early Start</TableCell>
                <TableCell>Early Finish</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>% Complete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {criticalActivities.map((activity) => (
                <TableRow key={activity._id}>
                  <TableCell>{activity.activityId}</TableCell>
                  <TableCell>{activity.name}</TableCell>
                  <TableCell>{activity.originalDuration}d</TableCell>
                  <TableCell>{new Date(activity.earlyStart).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(activity.earlyFinish).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={activity.status}
                      size="small"
                      color={getStatusColor(activity.status) as any}
                    />
                  </TableCell>
                  <TableCell>{activity.percentComplete}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading Primavera view...</Typography>
      </Box>
    );
  }

  // Handle case when no scheduleId is provided
  if (!scheduleId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Primavera P6 View
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 3 }}>
          Select a schedule to view Primavera P6 data
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {availableSchedules.length === 0 && !loading ? (
          <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <TimelineIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No Schedules Available
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph>
                There are no schedules available. Create a new schedule to get started with Primavera P6 data.
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<AccountTreeIcon />}
                  onClick={() => navigate('/project-planning')}
                >
                  Go to Project Planning
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/project-planning/schedules/new')}
                >
                  Create New Schedule
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
            {availableSchedules.map((sched) => (
              <Card 
                key={sched._id}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => navigate(`/project-planning/primavera/${sched._id}`)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="h2">
                      {sched.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {sched.description || 'No description available'}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip 
                      label={sched.status} 
                      size="small" 
                      color={sched.status === 'active' ? 'success' : 'default'}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/project-planning/primavera/${sched._id}`);
                      }}
                    >
                      View
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Primavera P6 View
          </Typography>
          {schedule && (
            <Typography variant="subtitle1" color="textSecondary">
              {schedule.name} - Project ID: {schedule.primaveraSettings?.projectId}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Import P6
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportP6}
          >
            Export P6
          </Button>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleCalculateCriticalPath}
            disabled={syncInProgress}
          >
            Calculate CPM
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
          >
            Settings
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {syncInProgress && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LinearProgress sx={{ flex: 1 }} />
            <Typography>Processing...</Typography>
          </Box>
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Overview" />
          <Tab label="Activities" />
          <Tab label="Critical Path" />
          <Tab label="Resources" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {renderMetrics()}
        
        {schedule && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Project Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2">Data Date</Typography>
                  <Typography variant="body2" gutterBottom>
                    {schedule.primaveraSettings?.dataDate ? 
                      new Date(schedule.primaveraSettings.dataDate).toLocaleDateString() : 'Not set'
                    }
                  </Typography>
                  
                  <Typography variant="subtitle2">Baseline Date</Typography>
                  <Typography variant="body2" gutterBottom>
                    {schedule.primaveraSettings?.baselineDate ? 
                      new Date(schedule.primaveraSettings.baselineDate).toLocaleDateString() : 'Not set'
                    }
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Finish Date</Typography>
                  <Typography variant="body2" gutterBottom>
                    {schedule.primaveraSettings?.finishDate ? 
                      new Date(schedule.primaveraSettings.finishDate).toLocaleDateString() : 'Not set'
                    }
                  </Typography>
                  
                  <Typography variant="subtitle2">Critical Path Method</Typography>
                  <Typography variant="body2" gutterBottom>
                    {schedule.primaveraSettings?.criticalPathMethod || 'CPM'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {renderActivitiesTable()}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {renderCriticalPath()}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Typography variant="h6" gutterBottom>
          Resource Management
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Resource leveling and allocation features would be implemented here.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AccountTreeIcon />}
            onClick={handleResourceLeveling}
            disabled={syncInProgress}
          >
            Perform Resource Leveling
          </Button>
        </Box>
      </TabPanel>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          // View activity details
          setAnchorEl(null);
        }}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          // Edit activity
          setAnchorEl(null);
        }}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Activity
        </MenuItem>
        <MenuItem onClick={() => {
          // Delete activity
          setAnchorEl(null);
        }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Activity
        </MenuItem>
      </Menu>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Primavera P6 File</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a Primavera P6 (.xer) file to import activities and schedule data.
          </Typography>
          <input
            type="file"
            accept=".xer,.xml"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleImportP6(file);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Primavera Settings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Configure Primavera P6 integration settings.
          </Typography>
          {/* Settings form would go here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button variant="contained">Save Settings</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrimaveraView;